<script lang="ts">
  import { createTrip } from './api';
  import AuthModal from './AuthModal.svelte';
  import InstallModal from './InstallModal.svelte';
  import BrandMark from './BrandMark.svelte';
  import { isStandalone } from './pwa';

  let starting = $state(false);
  let error: string | null = $state(null);
  let signInOpen: boolean = $state(false);
  let installOpen: boolean = $state(false);
  const showInstall = !isStandalone();

  // Reset transient state when the page is restored from the browser's
  // back-forward cache (e.g. user clicks back from a trip page).
  $effect(() => {
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) {
        starting = false;
        error = null;
      }
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  });

  async function startTrip() {
    starting = true;
    error = null;
    try {
      const { token } = await createTrip();
      window.location.assign(`/t/${token}`);
    } catch (e) {
      error = e instanceof Error ? e.message : 'Could not start a trip';
      starting = false;
    }
  }
</script>

<main class="hero-page">
  <div class="hero">
    <p class="brand">Lazy Traveler</p>
    <span class="brand-mark"><BrandMark size={28} /></span>
    <h1 class="hero-title">
      Bookmark places.<br />Decide later.
    </h1>
    <p class="hero-sub">
      A simple way to save the places you find, organize them in a few simple steps, and hit the road with Apple or Google Maps — and peace of mind.
    </p>
    <div class="hero-ctas">
      <button class="btn btn-primary hero-cta-primary" type="button" onclick={startTrip} disabled={starting}>
        {starting ? 'Starting…' : 'Start a trip'}
      </button>
      <button class="btn btn-tertiary" type="button" onclick={() => (signInOpen = true)}>
        Sign in
      </button>
    </div>
    {#if error}
      <p class="hero-error">{error}</p>
    {/if}
  </div>

  <footer class="hero-footer">
    {#if showInstall}
      <button class="footer-link" type="button" onclick={() => (installOpen = true)}>
        Install
      </button>
      <span class="footer-dot" aria-hidden="true">·</span>
    {/if}
    <a href="/privacy">Privacy</a>
  </footer>
</main>

<style>
  .hero-page {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    background: var(--color-canvas);
  }
  .hero {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 5rem 1.5rem 3rem;
    gap: 1.5rem;
  }
  .brand-mark {
    color: var(--color-ink);
    display: inline-flex;
  }
  /* Editorial-style wordmark — tracked-out uppercase reads as a
     deliberate brand mark without competing with the H1. */
  .brand {
    margin: 0 0 0.5rem;
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 0.28em;
    text-transform: uppercase;
    color: var(--color-muted);
  }
  .hero-title {
    margin: 0;
    font-size: clamp(40px, 7.5vw, 64px);
    font-weight: 700;
    line-height: 1.05;
    letter-spacing: -0.02em;
    color: var(--color-ink);
  }
  .hero-sub {
    margin: 0.5rem 0 0;
    max-width: 540px;
    font-size: 16px;
    line-height: 1.55;
    color: var(--color-body);
  }
  @media (min-width: 640px) {
    .hero { padding-top: 7rem; }
    .hero-sub { font-size: 17px; }
  }
  .hero-ctas {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.75rem;
    margin-top: 1.25rem;
  }
  .hero-cta-primary {
    min-width: 200px;
  }
  .hero-error {
    margin: 0;
    font-size: 13px;
    color: var(--color-error);
  }
  .hero-footer {
    padding: 1.5rem;
    text-align: center;
    font-size: 12px;
  }
  .hero-footer a,
  .hero-footer .footer-link {
    color: var(--color-muted);
    text-decoration: none;
    background: none;
    border: 0;
    padding: 0;
    font: inherit;
    cursor: pointer;
  }
  .hero-footer a:hover,
  .hero-footer .footer-link:hover {
    color: var(--color-ink);
  }
  .footer-dot {
    margin: 0 0.5rem;
    color: var(--color-muted);
  }
</style>

{#if signInOpen}
  <AuthModal onClose={() => (signInOpen = false)} />
{/if}

{#if installOpen}
  <InstallModal onClose={() => (installOpen = false)} />
{/if}
